import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './GalleryComponent.css';

interface GalleryImage {
  id?: string;
  url: string;
  title?: string;
  description?: string;
}

interface GalleryComponentProps extends WithTranslation {
  albums: GalleryImage[];
  maxImages?: number;
  goalId?: string;
  previousPage?: string;
}

const GalleryComponent: React.FC<GalleryComponentProps> = ({ 
  albums, 
  maxImages = 9,
  t,
  goalId,
  previousPage
}) => {
  const navigate = useNavigate();

  // Handle image click to navigate to GalleryViewerPage (matching reference project)
  const handleImageClick = (image: GalleryImage, index: number) => {
    // Navigate to GalleryViewerPage with albums data and initial page (matching reference project)
    navigate(`/goals/gallery/${goalId}`, { 
      state: { 
        albums: albums,
        initialPage: index,
        goalId: goalId,
        previousPage: previousPage
      } 
    });
  };

  // If no albums, show placeholder
  if (!albums || albums.length === 0) {
    return (
      <div className="gallery-component empty">
        <div className="gallery-placeholder">
          <p>{t ? t("gallery-component:placeholder.no-albums") : "Click comments to upload child's progress photos"}</p>
        </div>
      </div>
    );
  }

  // Limit images to maxImages
  const displayImages = albums.slice(0, maxImages);
  const hasMoreImages = albums.length > maxImages;

  return (
    <div className="gallery-component">
      <div className="gallery-grid">
        {displayImages.map((image, index) => (
          <div
            key={image.id || index}
            className="gallery-item"
            onClick={() => handleImageClick(image, index)}
          >
            <img
              key={image.id || index}
              src={image.url}
              alt={image.title || (t ? t("goal-component:image-alt", { index: index + 1 }) : `Goal progress ${index + 1}`)}
              className="gallery-image"
              loading="lazy"
            />
            {image.title && (
              <div className="image-overlay">
                <span className="image-title">{image.title}</span>
              </div>
            )}
          </div>
        ))}
        
        {/* Show more indicator if there are more images */}
        {hasMoreImages && (
          <div className="gallery-item more-indicator">
            <div className="more-content">
              <span className="more-text">+{albums.length - maxImages}</span>
              <span className="more-label">{t ? t("gallery-component:more.label") : "More"}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withTranslation()(GalleryComponent);
